
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { NodeShape } from "@/utils/shapeUtils";
import { getShapeTextFitStyle } from "@/utils/textFit";

type NodeContentProps = {
  label: string;
  nodeHeight: number;
  nodeWidth?: number;
  shape?: NodeShape;
  isSelected?: boolean;
  isEditingContent: boolean;
  isResizing: boolean;
  onContentClick: () => void;
  onContentChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onContentBlur: () => void;
  clientEdits?: {
    content: string;
    editedBy: 'client' | 'owner';
    timestamp: number;
  }[];
  isClientMode?: boolean;
};

const NodeContent = ({
  label,
  nodeHeight,
  nodeWidth,
  shape,
  isSelected = false,
  isEditingContent,
  isResizing,
  onContentClick,
  onContentChange,
  onContentBlur,
  clientEdits = [],
  isClientMode = false,
}: NodeContentProps) => {
  const contentFitStyle = getShapeTextFitStyle(shape ?? 'rectangle', nodeWidth ?? 0, nodeHeight);
  const contentColor = isClientMode && clientEdits.length > 0 ? 'hsl(var(--accent-foreground))' : 'hsl(var(--foreground))';
  const contentClass = `text-sm font-medium leading-relaxed cursor-text break-words whitespace-pre-wrap`;
  console.log("NodeContent rendering:", { 
    label, 
    labelLength: label?.length, 
    isEmpty: !label || label.trim() === "",
    isEditingContent,
    isSelected,
    shape,
  });

  const handleContentClick = (e: React.MouseEvent) => {
    console.log("Content area clicked, preventing propagation");
    e.preventDefault();
    e.stopPropagation();
    if (!isResizing) {
      onContentClick();
    }
  };

  const handleTextareaClick = (e: React.MouseEvent) => {
    console.log("Textarea clicked, preventing propagation");
    e.preventDefault();
    e.stopPropagation();
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    console.log("Textarea content changing:", e.target.value);
    e.stopPropagation();
    onContentChange(e);
  };

  const handleTextareaBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    console.log("Textarea losing focus");
    e.stopPropagation();
    onContentBlur();
  };

  const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    console.log("Textarea key pressed:", e.key);
    e.stopPropagation();
    
    if (e.key === 'Escape') {
      e.preventDefault();
      onContentBlur();
    }
    // Allow Enter for line breaks, don't prevent it
  };

  // Function to render text with client edits highlighted and proper wrapping
  const renderContentWithHighlights = () => {
    const hasContent = label && label.trim() !== "";
    
    if (hasContent) {
      return (
        <ScrollArea className="h-full w-full">
          <div 
            className={contentClass}
            style={{ 
              color: contentColor,
              wordWrap: "break-word",
              overflowWrap: "break-word",
              hyphens: "auto",
              ...contentFitStyle,
            }}
          >
            {label}
          </div>
        </ScrollArea>
      );
    }

    // Show placeholder when no content
    return (
      <div className="text-sm font-medium text-center text-muted-foreground leading-relaxed cursor-text" style={contentFitStyle}>
        Click to add text
      </div>
    );
  };

  return (
    <div 
      className="w-full flex-1"
      style={{
        backgroundColor: "transparent",
        position: 'relative',
        zIndex: 5
      }}
    >
      {isEditingContent ? (
        <div 
          className="nodrag h-full"
          style={{ 
            pointerEvents: 'all',
            height: `${nodeHeight - 30}px`,
            maxHeight: `${nodeHeight - 30}px`,
          }}
        >
          <ScrollArea className="h-full w-full">
            <Textarea
              autoFocus
              value={label}
              onChange={handleTextareaChange}
              onBlur={handleTextareaBlur}
              onKeyDown={handleTextareaKeyDown}
              onClick={handleTextareaClick}
              placeholder="Enter text..."
              className={`min-h-[40px] text-sm w-full border-none resize-none font-medium ${isClientMode ? 'client-edit-textarea' : ''}`}
              style={{ 
                height: `${nodeHeight - 50}px`,
                color: contentColor,
                backgroundColor: "transparent",
                pointerEvents: 'all',
                wordWrap: "break-word",
                overflowWrap: "break-word",
                ...contentFitStyle,
              }}
            />
          </ScrollArea>
        </div>
      ) : (
        <div 
          className="h-full cursor-text flex items-start justify-start" 
          onClick={handleContentClick}
          style={{ 
            height: `${nodeHeight - 30}px`,
            maxHeight: `${nodeHeight - 30}px`,
            minHeight: `40px`,
            overflow: 'auto',
            pointerEvents: 'all',
            ...contentFitStyle,
          }}
        >
          {renderContentWithHighlights()}
        </div>
      )}
    </div>
  );
};

export default NodeContent;
